// Low-level Web Audio synthesis primitives. Every UI sound is generated here
// at runtime — no audio files, no network, no bundle weight beyond this code.
//
// These helpers encode the dcs-product-sound implementation rules:
//   - gain-set-before-ramp    every ramp preceded by setValueAtTime()
//   - gain-no-zero-target     exponential ramps target 0.001, never 0
//   - capture-now-once        callers pass a single `now`; we derive from it
//   - stop-after-envelope     osc.stop(now + dur + tail) to avoid end clicks
//   - exponential-over-linear exponential ramps for gain/frequency
//   - node-cleanup            BufferSources disconnect their chain onended
//   - oscillator-for-tonal / noise-for-percussion
//   - volume-ceiling          callers clamp; filter Q ≤ 10 (no ringing)

/** Tail (seconds) added after the envelope end before stopping the node. */
const STOP_TAIL = 0.02
const MIN_GAIN = 0.0001

interface ToneOpts {
  freq: number
  type?: OscillatorType
  dur?: number
  gain?: number
  /** Attack time in seconds (soft by default — "felt mallet", not a click). */
  attack?: number
  /** Optional exponential pitch glide target by envelope end. */
  glideTo?: number
}

/** A single enveloped oscillator note (tonal roles: toggles, confirms, tones). */
export function playTone(
  ctx: AudioContext,
  dest: AudioNode,
  opts: ToneOpts,
  now: number,
): void {
  const { freq, type = 'sine', dur = 0.15, gain = 0.2, attack = 0.01, glideTo } = opts
  const osc = ctx.createOscillator()
  const amp = ctx.createGain()

  osc.type = type
  osc.frequency.setValueAtTime(freq, now)
  if (glideTo) {
    osc.frequency.exponentialRampToValueAtTime(glideTo, now + dur)
  }

  amp.gain.setValueAtTime(MIN_GAIN, now)
  amp.gain.exponentialRampToValueAtTime(gain, now + attack)
  amp.gain.exponentialRampToValueAtTime(0.001, now + dur)

  osc.connect(amp).connect(dest)
  osc.start(now)
  osc.stop(now + dur + STOP_TAIL)
  osc.onended = () => {
    try {
      osc.disconnect()
      amp.disconnect()
    } catch {
      /* already torn down */
    }
  }
}

interface BellOpts {
  freq: number
  /** Modulator:carrier frequency ratio. */
  ratio?: number
  /** FM index (modulation depth in Hz); decays to give a bell-like attack. */
  index?: number
  dur?: number
  gain?: number
}

/** FM bell — the notification/info character (bell-like, warm, non-alarming). */
export function playFmBell(
  ctx: AudioContext,
  dest: AudioNode,
  opts: BellOpts,
  now: number,
): void {
  const { freq, ratio = 2, index = 120, dur = 0.4, gain = 0.2 } = opts
  const carrier = ctx.createOscillator()
  const mod = ctx.createOscillator()
  const modGain = ctx.createGain()
  const amp = ctx.createGain()

  carrier.type = 'sine'
  mod.type = 'sine'
  carrier.frequency.setValueAtTime(freq, now)
  mod.frequency.setValueAtTime(freq * ratio, now)

  modGain.gain.setValueAtTime(index, now)
  modGain.gain.exponentialRampToValueAtTime(1, now + dur)

  amp.gain.setValueAtTime(MIN_GAIN, now)
  amp.gain.exponentialRampToValueAtTime(gain, now + 0.012)
  amp.gain.exponentialRampToValueAtTime(0.001, now + dur)

  mod.connect(modGain).connect(carrier.frequency)
  carrier.connect(amp).connect(dest)

  mod.start(now)
  carrier.start(now)
  mod.stop(now + dur + STOP_TAIL)
  carrier.stop(now + dur + STOP_TAIL)
  carrier.onended = () => {
    try {
      carrier.disconnect()
      mod.disconnect()
      modGain.disconnect()
      amp.disconnect()
    } catch {
      /* already torn down */
    }
  }
}

interface NoiseOpts {
  dur?: number
  gain?: number
  /** Bandpass centre frequency. */
  cutoff?: number
  /** Filter Q — kept ≤ 10 so the burst never rings. */
  q?: number
  /** Optional exponential filter sweep target (whoosh). */
  sweepTo?: number
}

/** Filtered noise burst — percussive character (taps, whooshes, transitions). */
export function playNoiseBurst(
  ctx: AudioContext,
  dest: AudioNode,
  opts: NoiseOpts,
  now: number,
): void {
  const { dur = 0.08, gain = 0.2, cutoff = 2000, q = 1, sweepTo } = opts
  const frames = Math.max(1, Math.floor(ctx.sampleRate * dur))
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < frames; i++) {
    data[i] = Math.random() * 2 - 1
  }

  const src = ctx.createBufferSource()
  src.buffer = buffer

  const filter = ctx.createBiquadFilter()
  filter.type = 'bandpass'
  filter.frequency.setValueAtTime(cutoff, now)
  filter.Q.value = Math.min(q, 10)
  if (sweepTo) {
    filter.frequency.exponentialRampToValueAtTime(sweepTo, now + dur)
  }

  const amp = ctx.createGain()
  amp.gain.setValueAtTime(gain, now)
  amp.gain.exponentialRampToValueAtTime(0.001, now + dur)

  src.connect(filter).connect(amp).connect(dest)
  src.start(now)
  src.stop(now + dur + STOP_TAIL)
  // BufferSources do not auto-disconnect — clean the whole chain on end.
  src.onended = () => {
    try {
      src.disconnect()
      filter.disconnect()
      amp.disconnect()
    } catch {
      /* already torn down */
    }
  }
}
